import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/base/dialog.tsx";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "../components/buttons/button.tsx";
import { cn } from "../utils/cn.ts";
import { FontStyles } from "../utils/font-styles.ts";
import { TextButton } from "../components/buttons/text-button/text-button.tsx";
import "../../assets/fa-icons/css/fontawesome.css";
import "../../assets/fa-icons/css/sharp-regular.css";
import { useWatchlist } from "./utils/use-watchlist.ts";
import { usePropertyDetailsContext } from "./contexts/property-details-context.tsx";

export const VISITS_KEY = "propertyVisits";
export const THRESHOLD = 3;
export const MAX_VISITS_ENTRIES = 50;

export const WatchlistModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { propertyId, propertySource, isAuthenticated } = usePropertyDetailsContext();
  const { isWatchlisted, toggle } = useWatchlist(propertyId, propertySource, "property_details");
  const modalText = isAuthenticated
    ? "Tap the heart icon to save this property to your watchlist and get instant alerts on price changes or closing dates."
    : "Sign up for a free account to save this property to your watchlist and get instant alerts on price changes or closing dates.";
  const buttonText = isAuthenticated ? "Save to Watchlist" : "Sign up";
  const initialIsWatchlisted = useRef(isWatchlisted);

  // If a property is not watchlisted and another property has not been visited 3 times, show the modal.
  // If the property is watchlisted, don't show the modal. Do not show this modal again if another
  // property is visited 3 times.
  useEffect(() => {
    if (initialIsWatchlisted.current) return;
    if (!propertyId) return;

    let visits = new Map<string, number>();
    try {
      const stored = JSON.parse(localStorage.getItem(VISITS_KEY) ?? "[]") as [string, number][];
      if (Array.isArray(stored)) visits = new Map(stored);
    } catch {
      visits = new Map();
    }
    //Check if the modal has already been displayed, if yes return
    for (const count of visits.values()) {
      if (count >= THRESHOLD) return;
    }

    const visitKey = propertySource ? `${String(propertyId)}_${propertySource}` : String(propertyId);

    const currentVisits = visits.get(visitKey) ?? 0;
    if (currentVisits < THRESHOLD) {
      const nextVisits = currentVisits + 1;
      // Delete then re-set so the just-visited key moves to the end of the
      // Map's insertion order, making it the most-recently-visited entry.
      // Eviction below then removes from the front (least-recently-visited).
      visits.delete(visitKey);
      visits.set(visitKey, nextVisits);
      while (visits.size > MAX_VISITS_ENTRIES) {
        const oldestKey = visits.keys().next().value;
        if (oldestKey === undefined) break;
        visits.delete(oldestKey);
      }
      try {
        localStorage.setItem(VISITS_KEY, JSON.stringify([...visits]));
      } catch {
        console.log("Could not write to local storage");
      }
      if (nextVisits === THRESHOLD) {
        setOpen(true);
      }
    }
  }, [propertyId, propertySource]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="twp p-0 max-w-fit w-fit border-none m-2.5 md:m-5" closeVariant="onDarkNeutral">
        <div className="flex flex-col rounded-lg bg-neutral-white max-w-[500px]">
          <img
            className="h-[200px] md:h-[240px] rounded-t-lg object-cover"
            src="/static/images/web-promo-pink.webp"
            alt="An ESPC promotional image showing two people posing in front of a property with a set of keys"
          />
          <div className="flex flex-col gap-4 p-3.5 md:p-7.5">
            <DialogTitle className={cn(FontStyles.h4, "font-semibold leading-none md:leading-none text-slate")}>
              Keep track of this property
            </DialogTitle>
            <DialogDescription className={cn(FontStyles["body-l"], "text-slate")}>{modalText}</DialogDescription>
            <Button
              renderLeadingIcon={isAuthenticated ? () => <i className="fa-sharp fa-regular fa-heart" /> : undefined}
              label={buttonText}
              variant={"solid"}
              colorVariant={"secondary"}
              onClick={() => {
                void toggle();
                setOpen(false);
              }}
            />
            <TextButton className="mx-auto block" onClick={() => setOpen(false)}>
              Maybe later
            </TextButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
